import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { default as ReactSelect, components } from 'react-select';

import ArrowDownPath from '../assets/stroke-drop-down.svg?react';
import ErrorIconPath from '../assets/stroked-circle-cross.svg?react';
import SuccessIconPath from '../assets/stroked-circle-check.svg?react';
import WarningIconPath from '../assets/stroke-circle-warning.svg?react';

export const StyleTypes = {
    default: {
        backgroundColor: 'var(--color-white)',
        borderColor: 'var(--gray200)',
        helperColor: 'var(--gray700)',
        hoverBackgroundColor: 'var(--gray50)',
        hoverBorderColor: 'var(--gray800)',
    },
    error: {
        backgroundColor: 'var(--red50)',
        borderColor: 'var(--color-error-default)',
        helperColor: 'var(--color-error-default)',
        hoverBackgroundColor: 'var(--red50)',
        hoverBorderColor: 'var(--color-error-default)',
    },
    success: {
        backgroundColor: 'var(--green50)',
        borderColor: 'var(--color-success-default)',
        helperColor: 'var(--color-success-default)',
        hoverBackgroundColor: 'var(--green50)',
        hoverBorderColor: 'var(--color-success-default)',
    },
    warning: {
        backgroundColor: 'var(--yellow50)',
        borderColor: 'var(--color-warning-default)',
        color: 'var(--color-warning-default)',
        helperColor: 'var(--yellow800)',
        hoverBackgroundColor: 'var(--yellow50)',
        hoverBorderColor: 'var(--color-warning-default)',
    },
};

export const Label = styled.p`
    color: var(--gray800);
    font-family: 'IBM Plex Sans';
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.32px;
    line-height: 16px;
    margin: 0 0 8px 0;
    text-transform: uppercase;
`;

const HelperContainer = styled.div`
    align-items: center;
    display: flex;
    margin: 8px 0 0 0;
`;

export const Helper = styled.p`
    color: ${(props) => props.helperStyle.helperColor};
    font-size: 14px;
    height: 16px;
    letter-spacing: 0.37px;
    line-height: 16px;
    margin: 0;
`;

export const HelperIconContainer = styled.div`
    margin-right: 8px;
    min-height: 16px;
    min-width: 16px;
`;

const ArrowDownContainer = styled.div`
    content: ' ';
    height: 100%;
    position: absolute;
    right: 12px;
    top: 12px;
    width: 18px;

    ${(props) =>
        props.opened &&
        css`
            top: -12px;
            right: 12px;
            transform: rotate(180deg);
        `};
`;

export const ArrowDown = styled(ArrowDownPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['var(--gray800)']};

        &[disabled] {
            fill: ${theme.colors['var(--gray200)']};
        }
    `}
`;

export const SuccessIcon = styled(SuccessIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-success-green-main']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const ErrorIcon = styled(ErrorIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-error-red-main']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const WarningIcon = styled(WarningIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-warning-yellow-dark']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const getCurrentStyle = (styleType) => {
    if (!StyleTypes.hasOwnProperty(styleType)) {
        return StyleTypes.default;
    }

    return StyleTypes[styleType];
};

const Select = ({ options = [], placeholder, styleType, disabled, label, helper, onChange, value, name, onTouched }) => {
    const [opened, setOpened] = useState(false);

    const currentStyle = getCurrentStyle(styleType);

    const DropdownIndicator = (localProps) => {
        return (
            <components.DropdownIndicator {...localProps}>
                <ArrowDownContainer opened={opened}>
                    <ArrowDown disabled={localProps.isDisabled} />
                </ArrowDownContainer>
            </components.DropdownIndicator>
        );
    };

    const badgeContent = () => {
        switch (styleType) {
            case 'error':
                return <ErrorIcon />;
            case 'success':
                return <SuccessIcon />;
            case 'warning':
                return <WarningIcon />;
            default:
                return null;
        }
    };

    const selectStyle = {
        menu: (styles) => ({
            ...styles,
            borderRadius: 0,
            margin: 0,
            overflow: 'visible',
        }),
        menuList: (styles) => ({
            ...styles,
            overflow: 'visible',
            paddingBottom: 0,
            paddingTop: 0,
        }),
        control: (styles, { isDisabled, isFocused, menuIsOpen, hasValue }) => ({
            ...styles,
            backgroundColor: currentStyle.backgroundColor,
            borderRadius: '0',
            borderStyle: 'solid',
            borderWidth: '1px',
            boxShadow: isFocused && !menuIsOpen ? '0 0 2px 2px var(--blue200)' : null,
            color: 'var(--gray700)',
            cursor: isDisabled ? 'initial' : 'pointer',
            height: '44px',
            borderColor: isDisabled
                ? 'var(--gray200)'
                : menuIsOpen
                  ? 'var(--blue500)'
                  : hasValue
                    ? 'var(--gray800)'
                    : currentStyle.borderColor,
            ':hover': {
                ...styles[':hover'],
                color: 'var(--gray800)',
                backgroundColor: currentStyle.hoverBackgroundColor,
                borderColor: currentStyle.hoverBorderColor,
            },
        }),
        placeholder: (styles) => {
            return {
                ...styles,
                color: 'inherit',
            };
        },
        indicatorSeparator: () => ({ display: 'none' }),
        option: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: isFocused ? 'var(--gray50)' : 'var(--color-white)',
                borderBottom: '1px solid var(--gray200)',
                boxShadow: isFocused ? '0 0 2px 2px var(--blue200)' : null,
                boxSizing: 'border-box',
                color: isDisabled ? 'var(--gray700)' : 'var(--gray800)',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans',
                fontSize: '16px',
                fontWeight: isSelected ? '600' : 'initial',
                height: '48px',
                letterSpacing: '0',
                lineHeight: '24px',
                padding: '12px 24px',
                ':hover': {
                    ...styles[':hover'],
                    boxShadow: 'none',
                    color: 'var(--blue700)',
                    backgroundColor: 'var(--gray50)',
                },
                overflow: 'visible',
                marginBottom: '2px',
                ':last-child': {
                    marginBottom: '0',
                    borderBottom: '0',
                },
            };
        },
        singleValue: (styles, { hasValue }) => {
            return {
                ...styles,
                color: opened ? 'var(--blue500)' : hasValue ? 'var(--gray800)' : 'var(--gray700)',
                ':hover': {
                    ...styles[':hover'],
                    color: 'var(--gray800)',
                },
            };
        },
    };
    return (
        <>
            {label && <Label>{label}</Label>}
            <ReactSelect
                components={{ DropdownIndicator }}
                isDisabled={disabled}
                isSearchable={false}
                name={name}
                onChange={onChange}
                onMenuClose={() => {
                    setOpened(false);
                    onTouched();
                }}
                onMenuOpen={() => setOpened(true)}
                options={options}
                placeholder={placeholder}
                styles={selectStyle}
                value={value}
            />
            <HelperContainer>
                {styleType !== 'default' && <HelperIconContainer>{badgeContent()}</HelperIconContainer>}
                <Helper helperStyle={currentStyle}>{helper}</Helper>
            </HelperContainer>
        </>
    );
};

Select.propTypes = {
    disabled: PropTypes.bool,
    helper: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    styleType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onTouched: PropTypes.func,
};

export default Select;
